import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Container,
  IconButton,
  MenuItem,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { getAuth, signOut } from 'firebase/auth';
import MenuIcon from '@mui/icons-material/Menu';
import { removeUser } from '../../../store/slices/userSlice';
import { useAuth } from '../../../hooks/use-auth';
import { disconnectSocket } from '../../../utils/socketManager';
import LanguageSwitcher from '../../common/LanguageSwitcher/LanguageSwitcher';
import {
  StyledAppBar,
  NavContainer,
  LogoContainer,
  MobileMenuButton,
  MobileMenu,
  MenuItemText,
  LogoLink,
  LogoText,
  NavLinksContainer,
  NavButton,
  UserContainer,
  UserMenu,
  UserIconButton,
} from './AppBar.styles';
import { isRouteActive } from './AppBar.config';

function ResponsiveAppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['navigation', 'common']);
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const pages = [{
    title: t('navigation:menu.costsOptimizer'),
    route: 'optimizer',
    activeRoutes: ['optimizer', 'optimizer-list', 'optimizer-costs-results', 'summarize'],
  },
  {
    title: t('navigation:menu.paymentCalendar'),
    route: 'calendar',
    activeRoutes: ['calendar'],
  },
  {
    title: t('navigation:menu.forecasting'),
    route: 'forecasting',
    activeRoutes: ['forecasting'],
  }];
  const settings = [
    {
      title: t('navigation:settings.logout'),
      route: 'logout',
    }];
  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseNavMenu = (_e: React.MouseEvent, page: string) => {
    setAnchorElNav(null);
    navigate(`/${page}`);
  };
  const handleCloseUserMenu = (_e: React.MouseEvent, page: string) => {
    setAnchorElUser(null);
    if (page === 'logout') {
      handleLogout();
    }
  };
  const auth = getAuth();
  const { isAuth } = useAuth();
  const dispatch = useDispatch();
  async function handleLogout() {
    disconnectSocket();
    await signOut(auth);
    dispatch(removeUser());
  }
  function handleLogin() {
    navigate('/signin');
  }
  return (
    <StyledAppBar position="static" className="app-bar">
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <NavContainer>
            <LogoContainer>
              <MobileMenuButton>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <MobileMenu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                >
                  {pages.map(({ title, route }) => (
                    <MenuItem key={route} onClick={(e) => handleCloseNavMenu(e, route)}>
                      <MenuItemText>{title}</MenuItemText>
                    </MenuItem>
                  ))}
                </MobileMenu>
              </MobileMenuButton>
              <LogoLink to="/" onClick={() => navigate('/optimizer')}>
                <img src="/logo.svg" alt="logo" className="logo" width={48} height={48} />
                <LogoText variant="h6">
                  {t('common:appName')}
                </LogoText>
              </LogoLink>
            </LogoContainer>
            <NavLinksContainer>
              {pages.map(({ title, route, activeRoutes }) => (
                <NavButton
                  key={route}
                  onClick={(e) => {
                    handleCloseNavMenu(e, route);
                  }}
                  isActive={isRouteActive(activeRoutes, location.pathname)}
                >
                  {title}
                </NavButton>
              ))}
            </NavLinksContainer>
            <UserContainer>
              <LanguageSwitcher />
              {isAuth ? (
                <Tooltip title="Open settings">
                  <UserIconButton onClick={handleOpenUserMenu}>
                    <Avatar alt="User Avatar" src="/static/images/avatar/2.jpg" />
                  </UserIconButton>
                </Tooltip>
              ) : (
                <Button onClick={handleLogin} variant="contained" color="primary">
                  {t('navigation:auth.signIn')}
                </Button>
              )}
              <UserMenu
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map(({ route, title }) => (
                  <MenuItem key={route} onClick={(e) => handleCloseUserMenu(e, route)}>
                    <MenuItemText>{title}</MenuItemText>
                  </MenuItem>
                ))}
              </UserMenu>
            </UserContainer>
          </NavContainer>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
}
export default ResponsiveAppBar;
